import { useState } from "react";
import { motion } from "framer-motion";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useAccount } from "wagmi";
import { formatEther, parseEther } from "viem";
import {
    Coins, Loader2, Check, AlertTriangle, ArrowUpRight, LogOut, TrendingUp,
} from "lucide-react";
import {
    useStakingPosition,
    useApprovePiggy, useStakePiggy, useUnstakePiggy,
    useClaimPiggyStaking, useExitPiggyStaking,
} from "@/hooks/useStaking";
import { ADDRESSES, CHAIN_ID, EXPLORER_TX } from "@/lib/config";
import { useFireOnConfirm } from "@/hooks/useFireOnConfirm";
import { useClaimedHistory } from "@/hooks/useClaimedHistory";
import { fmtCompact } from "@/lib/format";
import { CountUp } from "@/components/CountUp";
import { PiggyMark, EthMark, TokenBadge } from "@/components/Logo";

export default function Stake() {
    const { isConnected, chainId } = useAccount();
    const wrongChain = isConnected && chainId !== CHAIN_ID;
    const noContract = !ADDRESSES.PIGGY_STAKING || !ADDRESSES.PIGGY_TOKEN;
    const { pos, refetch } = useStakingPosition();

    const [stakeInput, setStakeInput] = useState("");
    const [unstakeInput, setUnstakeInput] = useState("");

    const stakeAmount = safeParse(stakeInput);
    const unstakeAmount = safeParse(unstakeInput);

    const needsApproval = stakeAmount > 0n && pos.allowance < stakeAmount;

    return (
        <section className="max-w-6xl mx-auto px-6 py-16">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-12"
            >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-pink mb-4 text-xs font-semibold text-piggy-pink-200">
                    <Coins className="w-3 h-3" />
                    20% of every taxed swap → PIGGY stakers
                </div>
                <h1 className="display text-5xl md:text-6xl font-black tracking-tighter mb-4">
                    Stake PIGGY. <span className="gradient-text-shimmer">Earn ETH.</span>
                </h1>
                <p className="text-piggy-cream/60 text-lg max-w-2xl mx-auto">
                    Single-sided staking. No lockup, no impermanent loss. Just
                    commit your PIGGY and earn ETH pro-rata from every taxed swap.
                    Withdraw anytime — no strings on this bow.
                </p>
            </motion.div>

            {noContract ? (
                <NoticeBox>
                    Staking contract not yet deployed. Set{" "}
                    <code className="text-piggy-pink-300">VITE_PIGGY_STAKING_ADDRESS</code>{" "}
                    and <code className="text-piggy-pink-300">VITE_PIGGY_TOKEN_ADDRESS</code>{" "}
                    in your .env after testnet deploy.
                </NoticeBox>
            ) : (
                <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
                    <PositionCard pos={pos} connected={isConnected} />
                    <div className="space-y-6">
                        <StakeForm
                            input={stakeInput}
                            setInput={setStakeInput}
                            amount={stakeAmount}
                            needsApproval={needsApproval}
                            balance={pos.walletBalance}
                            connected={isConnected}
                            wrongChain={wrongChain}
                            onSuccess={refetch}
                        />
                        <UnstakeForm
                            input={unstakeInput}
                            setInput={setUnstakeInput}
                            amount={unstakeAmount}
                            staked={pos.stakedAmount}
                            connected={isConnected}
                            wrongChain={wrongChain}
                            onSuccess={refetch}
                        />
                        <RewardsCard earned={pos.earned}
                                     hasStake={pos.stakedAmount > 0n}
                                     connected={isConnected}
                                     wrongChain={wrongChain}
                                     onSuccess={refetch} />
                    </div>
                </div>
            )}

            <ClaimedHistoryCard />

            <PoolStats pos={pos} />
        </section>
    );
}

function ClaimedHistoryCard() {
    const { isConnected } = useAccount();
    const { data } = useClaimedHistory();

    const num = (v: bigint) => Number(formatEther(v));
    const fmtEth = (v: number) => (v === 0 ? "0" : v >= 0.001 ? v.toFixed(4) : v.toFixed(6));
    const total = data ? num(data.total) : 0;
    const rows = data
        ? [
              { label: "PIGGY staking", v: num(data.sources.piggyStaking) },
              { label: "LP staking",    v: num(data.sources.lpStaking) },
              { label: "Reflections",   v: num(data.sources.reflection) },
              { label: "Referrals",     v: num(data.sources.referral) },
          ]
        : [];

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="mt-6 glass rounded-3xl p-6"
        >
            <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-piggy-pink-300" />
                <h3 className="display text-lg font-black">Your lifetime claimed ETH</h3>
            </div>
            {!isConnected ? (
                <p className="text-sm text-piggy-cream/50">
                    Connect your wallet to track how much ETH you've claimed across every reward path.
                </p>
            ) : (
                <>
                    <div className="flex items-baseline gap-2 mb-4">
                        <span className="display text-4xl font-black gradient-text">{fmtEth(total)}</span>
                        <span className="text-piggy-cream/50 text-sm">ETH claimed all-time</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {rows.map((r) => (
                            <div key={r.label} className="rounded-2xl bg-white/[0.03] p-3">
                                <div className="text-xs text-piggy-cream/40 mb-1">{r.label}</div>
                                <div className="font-semibold text-piggy-cream">{fmtEth(r.v)} ETH</div>
                            </div>
                        ))}
                    </div>
                    {total === 0 && (
                        <p className="text-xs text-piggy-cream/40 mt-3">
                            No claims yet — every ETH reward you claim from staking, reflections, or
                            referrals is tallied here.
                        </p>
                    )}
                </>
            )}
        </motion.div>
    );
}

function safeParse(input: string): bigint {
    try {
        if (!input || input === ".") return 0n;
        return parseEther(input);
    } catch {
        return 0n;
    }
}

function PositionCard({ pos, connected }: { pos: ReturnType<typeof useStakingPosition>["pos"]; connected: boolean }) {
    const shareBps = pos.totalStaked > 0n
        ? Number((pos.stakedAmount * 10_000n) / pos.totalStaked)
        : 0;

    return (
        <div className="glass rounded-3xl p-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-4">
                Your position
            </div>

            {!connected && (
                <div className="text-xs text-piggy-cream/40 mb-4">
                    Connect your wallet to see your position.
                </div>
            )}

            <div className="space-y-4">
                <BigStat label="Staked"
                         value={<CountUp value={Number(pos.stakedAmount) / 1e18} format={(n) => fmtCompact(n)} />}
                         unit="PIGGY" />
                <BigStat label="Unclaimed ETH"
                         value={<CountUp value={Number(pos.earned) / 1e18} format={(n) => n.toFixed(5)} />}
                         unit="ETH" accent />
                <BigStat label="Your share of pool"
                         value={`${(shareBps / 100).toFixed(2)}%`} />
                <BigStat label="Wallet balance"
                         value={<CountUp value={Number(pos.walletBalance) / 1e18} format={(n) => fmtCompact(n)} />}
                         unit="PIGGY" />
            </div>
        </div>
    );
}

function BigStat({
    label, value, unit, accent,
}: {
    label: string; value: React.ReactNode; unit?: string; accent?: boolean;
}) {
    return (
        <div className={`rounded-2xl p-4 ${accent ? "bg-piggy-pink-500/[0.08] ring-1 ring-piggy-pink-500/30" : "bg-white/[0.03]"}`}>
            <div className="text-xs text-piggy-cream/40 uppercase tracking-wider mb-1">{label}</div>
            <div className="flex items-baseline gap-2">
                <div className={`display text-2xl font-bold ${accent ? "gradient-text" : "text-piggy-cream"}`}>
                    {value}
                </div>
                {unit && (
                    <div className="text-xs text-piggy-cream/40 inline-flex items-center gap-1.5">
                        {unit === "PIGGY" && <PiggyMark size={16} className="rounded-full" />}
                        {unit === "ETH" && <EthMark size={16} />}
                        <span>{unit}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

function StakeForm({
    input, setInput, amount, needsApproval, balance, connected, wrongChain, onSuccess,
}: {
    input: string; setInput: (s: string) => void;
    amount: bigint; needsApproval: boolean; balance: bigint;
    connected: boolean; wrongChain: boolean;
    onSuccess: () => void;
}) {
    const approve = useApprovePiggy();
    const stake = useStakePiggy(amount);
    const approveBusy = approve.status === "pending" || approve.status === "confirming";
    const stakeBusy   = stake.status   === "pending" || stake.status   === "confirming";

    useFireOnConfirm(approve.status === "confirmed" || stake.status === "confirmed", onSuccess);

    const overBalance = amount > balance;

    return (
        <div className="glass rounded-3xl p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-3">
                Stake PIGGY
            </div>
            <AmountInput
                value={input} onChange={setInput}
                onMax={() => setInput(formatEther(balance))}
                placeholder="0.0"
            />
            <div className="text-xs text-piggy-cream/40 mt-2 mb-5 flex justify-between">
                <span className="inline-flex items-center gap-1">
                    Wallet: {fmtCompact(Number(balance) / 1e18)} <TokenBadge symbol="PIGGY" size={12} />
                </span>
                <span>{overBalance && <span className="text-red-400">Exceeds balance</span>}</span>
            </div>

            <GateButton connected={connected} wrongChain={wrongChain} action="stake">
                {needsApproval ? (
                    <button
                        onClick={approve.approve}
                        disabled={approveBusy}
                        className="w-full btn-primary justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                        {approveBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                        {approveBusy ? "Approving..." : "Approve PIGGY"}
                    </button>
                ) : (
                    <button
                        onClick={stake.stake}
                        disabled={stakeBusy || amount === 0n || overBalance}
                        className="w-full btn-primary justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                        {stakeBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                        {stake.status === "confirmed" && <Check className="w-4 h-4" />}
                        {stakeBusy ? "Confirming..." : stake.status === "confirmed" ? "Staked!" : "Stake"}
                    </button>
                )}
            </GateButton>
            <TxLink hash={approve.hash || stake.hash} />
        </div>
    );
}

function UnstakeForm({
    input, setInput, amount, staked, connected, wrongChain, onSuccess,
}: {
    input: string; setInput: (s: string) => void;
    amount: bigint; staked: bigint;
    connected: boolean; wrongChain: boolean;
    onSuccess: () => void;
}) {
    const unstake = useUnstakePiggy(amount);
    const busy = unstake.status === "pending" || unstake.status === "confirming";
    useFireOnConfirm(unstake.status === "confirmed", onSuccess);
    const overStake = amount > staked;

    return (
        <div className="glass rounded-3xl p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-3">
                Unstake PIGGY
            </div>
            <AmountInput
                value={input} onChange={setInput}
                onMax={() => setInput(formatEther(staked))}
                placeholder="0.0"
            />
            <div className="text-xs text-piggy-cream/40 mt-2 mb-5 flex justify-between">
                <span className="inline-flex items-center gap-1">
                    Staked: {fmtCompact(Number(staked) / 1e18)} <TokenBadge symbol="PIGGY" size={12} />
                </span>
                <span>{overStake && <span className="text-red-400">Exceeds stake</span>}</span>
            </div>
            <GateButton connected={connected} wrongChain={wrongChain} action="unstake">
                <button
                    onClick={unstake.unstake}
                    disabled={busy || amount === 0n || overStake}
                    className="w-full btn-ghost justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    {unstake.status === "confirmed" && <Check className="w-4 h-4" />}
                    {busy ? "Confirming..." : unstake.status === "confirmed" ? "Unstaked!" : "Unstake"}
                </button>
            </GateButton>
            <TxLink hash={unstake.hash} />
        </div>
    );
}

function RewardsCard({
    earned, hasStake, connected, wrongChain, onSuccess,
}: {
    earned: bigint; hasStake: boolean; connected: boolean; wrongChain: boolean; onSuccess: () => void;
}) {
    const claim = useClaimPiggyStaking();
    const exit = useExitPiggyStaking();
    const claimBusy = claim.status === "pending" || claim.status === "confirming";
    const exitBusy  = exit.status  === "pending" || exit.status  === "confirming";
    useFireOnConfirm(claim.status === "confirmed" || exit.status === "confirmed", onSuccess);

    return (
        <div className="glass rounded-3xl p-6">
            <div className="flex items-baseline justify-between mb-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50">
                    Claim rewards
                </div>
                <div className="text-xs text-piggy-cream/40 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Live accrual
                </div>
            </div>
            <div className="display text-4xl font-black gradient-text mb-5 inline-flex items-center gap-2">
                {formatEther(earned).slice(0, 10)} <EthMark size={28} />
                <span>ETH</span>
            </div>
            <GateButton connected={connected} wrongChain={wrongChain} action="claim">
                <div className="grid grid-cols-2 gap-2">
                    <button
                        onClick={claim.claim}
                        disabled={claimBusy || earned === 0n}
                        className="btn-gold justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                        {claimBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                        {claim.status === "confirmed" && <Check className="w-4 h-4" />}
                        {claimBusy ? "..." : "Claim"}
                    </button>
                    <button
                        onClick={exit.exit}
                        disabled={exitBusy || !hasStake}
                        className="btn-ghost justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                        {exitBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                        {!exitBusy && <LogOut className="w-4 h-4" />}
                        Exit
                    </button>
                </div>
            </GateButton>
            <TxLink hash={claim.hash || exit.hash} />
        </div>
    );
}

function AmountInput({
    value, onChange, onMax, placeholder,
}: {
    value: string; onChange: (s: string) => void; onMax: () => void; placeholder: string;
}) {
    return (
        <div className="relative">
            <input
                type="text"
                inputMode="decimal"
                value={value}
                onChange={(e) => {
                    const sanitized = e.target.value.replace(/[^0-9.]/g, "");
                    if ((sanitized.match(/\./g) ?? []).length > 1) return;
                    onChange(sanitized);
                }}
                placeholder={placeholder}
                className="w-full px-4 py-4 pr-20 rounded-2xl bg-white/[0.04] border border-white/10 focus:border-piggy-pink-500/40 outline-none text-2xl font-bold display text-piggy-cream"
            />
            <button
                onClick={onMax}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-xl bg-piggy-pink-500/20 hover:bg-piggy-pink-500/30 text-piggy-pink-200 text-xs font-semibold"
            >
                MAX
            </button>
        </div>
    );
}

function TxLink({ hash }: { hash?: `0x${string}` }) {
    if (!hash) return null;
    return (
        <a
            href={`${EXPLORER_TX}/${hash}`}
            target="_blank" rel="noopener noreferrer"
            className="mt-3 flex items-center justify-center gap-1 text-xs text-piggy-cream/50 hover:text-piggy-cream"
        >
            View on Explorer <ArrowUpRight className="w-3 h-3" />
        </a>
    );
}

function PoolStats({ pos }: { pos: ReturnType<typeof useStakingPosition>["pos"] }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4"
        >
            <div className="glass rounded-2xl p-6">
                <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-2">
                    Total PIGGY staked
                </div>
                <div className="display text-3xl font-bold text-piggy-cream inline-flex items-center gap-2">
                    <CountUp value={Number(pos.totalStaked) / 1e18} format={(n) => fmtCompact(n)} />
                    <PiggyMark size={22} className="rounded-md" />
                    <span>PIGGY</span>
                </div>
            </div>
            <div className="glass rounded-2xl p-6">
                <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-2">
                    Lifetime ETH distributed
                </div>
                <div className="display text-3xl font-bold gradient-text inline-flex items-center gap-2">
                    <CountUp value={Number(pos.totalReceived) / 1e18} format={(n) => n.toFixed(4)} />
                    <EthMark size={22} />
                    <span>ETH</span>
                </div>
            </div>
        </motion.div>
    );
}

// Shared action-button gate: when disconnected → a connect button; when on the
// wrong chain → a switch button; otherwise the real action passed as children.
function GateButton({
    connected, wrongChain, action, children,
}: {
    connected: boolean; wrongChain: boolean; action: string; children: React.ReactNode;
}) {
    if (!connected) {
        return (
            <ConnectButton.Custom>
                {({ openConnectModal }) => (
                    <button onClick={openConnectModal} className="w-full btn-primary justify-center">
                        Connect wallet to {action}
                    </button>
                )}
            </ConnectButton.Custom>
        );
    }
    if (wrongChain) {
        return (
            <ConnectButton.Custom>
                {({ openChainModal }) => (
                    <button onClick={openChainModal} className="w-full btn-primary justify-center">
                        Switch to Robinhood Chain
                    </button>
                )}
            </ConnectButton.Custom>
        );
    }
    return <>{children}</>;
}

function NoticeBox({ children }: { children: React.ReactNode }) {
    return (
        <div className="glass-pink rounded-3xl p-6 flex gap-3 items-start max-w-2xl mx-auto">
            <AlertTriangle className="w-5 h-5 text-piggy-pink-300 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-piggy-cream/80">{children}</div>
        </div>
    );
}
